// The channel a reply goes out on, in the portal's words (module 04 D259 part 1; module 10 DESIGN
// B1555). It is the same table as `apps/api/src/draft/channel.ts` and it says the same things; the
// two are held apart because this half carries the words a person reads and the API half carries
// the words a model reads, and neither should be able to change the other by accident. What binds
// them is `channel.test.ts` here and there: each asserts that what the chip promises is what Send
// does, so a table that grows a second opinion fails on this side too.
//
// What this closes: today the panel branches on `teams` alone, so a Cobrain messenger message and
// a WhatsApp message both fall into the MAIL branch -- drawn with a copy list, an Outlook button
// and a Send that puts a chat message in the post.

/** What the view's `channel` can be. 'other' is what an older row says; it is read as unknown. */
export type ReplyChannel = 'email' | 'teams' | 'messenger' | 'whatsapp' | 'unknown' | 'none';

export interface ChannelWords {
  /** The heading: "Your reply in Teams to Will". */
  heading: (to: string | null) => string;
  /** The line under the heading. It says what happens on Send, and never promises what cannot be done. */
  rule: string;
  /** Whether Cobrain itself puts this message where it is going. */
  cobrainSends: boolean;
  /** The words on the button that does it, naming the person (B1560: the promise and the button match). */
  sendLabel: (to: string | null) => string;
  /** True where a mail's furniture belongs: the copy list, the thread line, the Outlook copy. */
  mailFurniture: boolean;
}

const NOTHING_SENT = 'Nothing is sent until you press the button.';

export const CHANNEL_WORDS: Readonly<Record<ReplyChannel, ChannelWords>> = {
  email: {
    heading: (to) => `Your reply${to ? ` to ${to}` : ''}`,
    rule: `Written in your measured voice from your own mail. Cobrain's own drafts never join that corpus. ${NOTHING_SENT}`,
    cobrainSends: true,
    sendLabel: (to) => (to ? `Send it to ${to}` : 'Send it'),
    mailFurniture: true,
  },
  teams: {
    heading: (to) => `Your reply in Teams${to ? ` to ${to}` : ''}`,
    rule: `Written in your measured voice, for a Teams message. ${NOTHING_SENT}`,
    cobrainSends: true,   // only where the one-off consent is held; the panel asks and draws the button then
    sendLabel: (to) => (to ? `Send it to ${to} in Teams` : 'Send it in Teams'),
    mailFurniture: false,
  },
  messenger: {
    // The messenger is ours, so an item that says "you send it" can mean it. Whether the SEND is
    // wired is module 10 DESIGN Open 86f: until it is, the panel offers the copy and says so
    // rather than drawing a button that would put a chat message in the post.
    heading: (to) => `Your reply in the messenger${to ? ` to ${to}` : ''}`,
    rule: `Written in your measured voice, for a message in the chat it came from. ${NOTHING_SENT}`,
    cobrainSends: false,
    sendLabel: () => 'Copy the reply',
    mailFurniture: false,
  },
  whatsapp: {
    heading: (to) => `Your reply on WhatsApp${to ? ` to ${to}` : ''}`,
    // Said plainly, because the alternative is what happens today: a WhatsApp message answered by
    // email without a word to anybody. "Yet" is not a promise of a date, it is why the copy exists.
    rule: 'Written in your measured voice, for a WhatsApp message. Cobrain does not send on WhatsApp yet, so copy it and send it there yourself.',
    cobrainSends: false,
    sendLabel: () => 'Copy the reply',
    mailFurniture: false,
  },
  unknown: {
    heading: (to) => `Your message${to ? ` to ${to}` : ''}`,
    rule: `This did not arrive as a mail, so your reply goes as a new mail under its own subject. ${NOTHING_SENT}`,
    cobrainSends: true,
    sendLabel: (to) => (to ? `Send it to ${to}` : 'Send it'),
    mailFurniture: true,
  },
  none: {
    heading: () => 'Your note',
    rule: 'A note to yourself, in your own words. It goes to nobody.',
    cobrainSends: false,
    // 'Copy the note', not 'Copy the reply': it is not a reply, and not 'Keep it' either, because
    // that would be a button that says one thing and copies. Found by opening a note on the page.
    sendLabel: () => 'Copy the note',
    mailFurniture: false,
  },
};

/**
 * A note that goes to someone other than the writer (BUGS.md T26, module 04 B2201): "Ask <name> for their notes" on a call
 * Cobrain did not get into opens a draft to them, which the person sends (Mike by pop-up, 6 Oct). The API mails a note to
 * its recipients on Send (draft/channel.ts, `none`: sendPath mailbox), so this one is drawn as a mail to them, with its copy
 * list and a Send, where every other note is a note to oneself.
 */
export const NOTE_TO_WORDS: ChannelWords = {
  heading: (to) => `Your note${to ? ` to ${to}` : ''}`,
  rule: `It goes as a new mail under its own subject. ${NOTHING_SENT}`,
  cobrainSends: true,
  sendLabel: (to) => (to ? `Send it to ${to}` : 'Send it'),
  mailFurniture: true,
};

/** The view's `channel`, read for what this build understands; anything else is a message of unknown origin. */
export function channelOf(v: { channel?: string | null } | null | undefined): ReplyChannel {
  const c = v?.channel;
  if (c === 'email' || c === 'teams' || c === 'messenger' || c === 'whatsapp' || c === 'none') return c;
  // 'other' is what every build before this one wrote for a messenger or a WhatsApp message, and
  // it is the value that used to fall through to the mail branch. It is unknown, not email.
  return 'unknown';
}

export function channelWords(c: ReplyChannel): ChannelWords {
  return CHANNEL_WORDS[c];
}

export const REPLY_CHANNELS: readonly ReplyChannel[] = ['email', 'teams', 'messenger', 'whatsapp', 'unknown', 'none'];

/**
 * Everyone a chat reply reaches, in words (module 04 DESIGN B1831; REQ-2026-09-24-46, Mike's "Name everyone"): "Chris
 * Watts and Renaldo Meere", "A, B and C", and "and 1 other" for a member Cobrain has no address for. Null when nobody is
 * named, so the caller keeps its own words rather than inventing a person.
 */
export function peopleWord(names: readonly string[], others = 0): string | null {
  const list = names.map((n) => n.trim()).filter((n) => n.length > 0);
  if (list.length === 0) return null;
  const parts = others > 0 ? [...list, `${others} other${others === 1 ? '' : 's'}`] : list;
  return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** The chat's people a Teams option carries (the API's rank/options.ts withChatPeople), or null. */
export function chatOf(params: Record<string, unknown> | null | undefined): { names: string[]; others: number } | null {
  const c = params?.chat as { names?: unknown; others?: unknown } | undefined;
  if (!c || !Array.isArray(c.names)) return null;
  const names = c.names.filter((n): n is string => typeof n === 'string' && n.trim().length > 0);
  const others = typeof c.others === 'number' && c.others > 0 ? Math.floor(c.others) : 0;
  return { names, others };
}
